import {fetchJSONMemoized} from '@gaia/utils/fetchJSON';
import {fetchLayerTypes} from '@gaia/utils/gaia/fetchLayerTypes';
import {getLocalStorageItem} from '@gaia/utils/localStorage';
import differenceBy from 'lodash/differenceBy';
import intersectionBy from 'lodash/intersectionBy';
import invert from 'lodash/invert';
import mapValues from 'lodash/mapValues';

import {fetchDefaultUserLayers} from './fetchDefaultUserLayers';

interface fetchUserLayersArgs {
    urlLayers: string[] | undefined;
    isAuthenticated: boolean;
    hasSubscription: boolean;
    canAccessNonPublicSources: boolean;
}

export async function fetchUserLayers({
    urlLayers,
    isAuthenticated,
    hasSubscription,
    canAccessNonPublicSources,
}: fetchUserLayersArgs): Promise<{
    savedUserLayers: UserLayer[] | undefined;
    userLayers: UserLayer[] | undefined;
    isUserLayersEphemeral: boolean;
}> {
    const savedUserLayers = await fetchSavedUserLayers({
        isAuthenticated,
        hasSubscription,
        canAccessNonPublicSources,
    });

    if (urlLayers && urlLayers.length && savedUserLayers) {
        // URL layers use the user-facing displayKey, which doesn't always match the id/uniqueTilecacheKey
        // that is used to reference layers internally. For example, USGS Topo has a uniqueTilecacheKey of 'CalTopo'
        // but a display key of 'usgs-topo'. These layers need to be mapped back to their uniqueTilecacheKey
        // in order to correctly identify them in state.
        const layerTypes = await fetchLayerTypes({
            isAuthenticated,
            canAccessNonPublicSources,
        });

        // Object with format {displayKey: uniqueTilecacheKey}
        const displayKeys = invert(mapValues(layerTypes, (layerType) => layerType.displayKey));

        const savedOpacities = Object.fromEntries(
            savedUserLayers.map((userLayer) => [userLayer.layer.type, userLayer.layer.opacity])
        );

        const userLayerMap = new Map<string, UserLayer>();

        // Convert url layer display keys to userLayers and use the opacity settings from localStorage, if available
        urlLayers.forEach((type: string) => {
            const normalizedType = layerTypes[type] ? type : displayKeys[type];
            if (!normalizedType) return;
            userLayerMap.set(normalizedType, {
                isVisible: true,
                layer: {
                    type: normalizedType,
                    opacity: savedOpacities[normalizedType] ?? 1,
                },
            });
        });

        // Finally, add any saved layers that aren't already present in the URL, and set them to hidden
        savedUserLayers.forEach((userLayer) => {
            const {type} = userLayer.layer;
            if (!userLayerMap.has(type)) {
                userLayerMap.set(type, {
                    ...userLayer,
                    isVisible: false,
                });
            }
        });

        return {
            isUserLayersEphemeral: true,
            savedUserLayers,
            userLayers: Array.from(userLayerMap.values()),
        };
    } else {
        return {
            isUserLayersEphemeral: false,
            savedUserLayers,
            userLayers: savedUserLayers,
        };
    }
}

interface fetchSavedUserLayersArgs {
    isAuthenticated: boolean;
    hasSubscription: boolean;
    canAccessNonPublicSources: boolean;
}

async function fetchSavedUserLayers({
    isAuthenticated,
    hasSubscription,
    canAccessNonPublicSources,
}: fetchSavedUserLayersArgs) {
    if (isAuthenticated) {
        const localUserLayers = getLocalStorageItem<UserLayer[]>(
            'userLayers',
            [],
            isValidUserLayers
        );

        const remoteUserLayers = (
            await fetchJSONMemoized('/api/objects/mapSource/tilejson/', {
                credentials: 'same-origin',
            })
        ).map((tilejson: any) => ({
            isVisible: false,
            layer: {
                opacity: 1,
                type: tilejson.uniqueTilecacheKey,
            },
        })) as UserLayer[];

        const userLayers = [
            ...intersectionBy(localUserLayers, remoteUserLayers, 'layer.type'),
            ...differenceBy(remoteUserLayers, localUserLayers, 'layer.type'),
        ];

        if (userLayers.length) return userLayers;
    }

    return await fetchDefaultUserLayers({hasSubscription, canAccessNonPublicSources});
}

function isValidUserLayers(userLayers: UserLayer[]) {
    if (!Array.isArray(userLayers)) return false;

    return userLayers.every(
        (userLayer) =>
            userLayer.isVisible === Boolean(userLayer.isVisible) && // tests if `isVisible` is boolean
            typeof userLayer.layer.type === 'string' &&
            typeof userLayer.layer.opacity === 'number'
    );
}
